import { Flex, Text } from '@chakra-ui/react';
import {
  type FormatOptionLabelMeta,
  type OptionBase,
  Select,
  type SingleValue,
} from 'chakra-react-select';

import { useChartTheme } from '@revelio/replots';

import { getFilterDefinition } from '../engine/filterRegistry';
import type { FilterId, FilterSelectOptions } from '../types/filters';

interface FilterOption extends OptionBase {
  label: string;
  value: FilterSelectOptions['id'];
  labelPostfix?: string;
}

interface FilterSelectDropdownProps {
  filterOptions: FilterSelectOptions[];
  selectedFilter: FilterId | 'AI_SEARCH' | null;
  onChangeSelectedFilter: (selected: SingleValue<FilterOption>) => void;
}

export const FilterSelectDropdown = ({
  filterOptions,
  selectedFilter,
  onChangeSelectedFilter,
}: FilterSelectDropdownProps) => {
  const { isNewTheme } = useChartTheme();
  // Get the selected filter name from the definition (handles child filters not in filterOptions)
  const selectedFilterDef =
    selectedFilter && selectedFilter !== 'AI_SEARCH'
      ? getFilterDefinition(selectedFilter)
      : null;
  const selectedFilterOption = filterOptions.find(
    (f) => f.id === selectedFilter
  );
  const selectedFilterName =
    selectedFilterDef?.name || selectedFilterOption?.name || '';
  const selectedFilterPostfix = selectedFilterOption?.labelPostfix;

  const formatOptionLabel = (
    option: FilterOption,
    meta: FormatOptionLabelMeta<FilterOption>
  ) => {
    if (meta.context !== 'value' || !option.labelPostfix) {
      return <Text fontWeight={500}>{option.label}</Text>;
    }

    return (
      <Flex>
        <Text fontWeight={500}>{option.label}</Text>
        <Text ml={1} fontWeight={500} color="gray.400">
          {option.labelPostfix}
        </Text>
      </Flex>
    );
  };

  return (
    <Select<FilterOption>
      id="filter-type-select"
      options={filterOptions.map((option) => ({
        label: option.name,
        value: option.id,
        labelPostfix: option.labelPostfix,
      }))}
      value={
        selectedFilter
          ? {
              label: selectedFilterName,
              value: selectedFilter,
              labelPostfix: selectedFilterPostfix,
            }
          : null
      }
      formatOptionLabel={formatOptionLabel}
      onChange={(selected) => onChangeSelectedFilter(selected)}
      size="sm"
      selectedOptionColorScheme="green"
      menuPortalTarget={document.body}
      // 1000 is chakra's default dropdown z-index
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 1000 }) }}
      chakraStyles={{
        control: (provider) => ({
          ...provider,
          height: '30px',
          fontWeight: 400,
        }),
        menu: (provider) => ({
          ...provider,
          ...(isNewTheme && {
            backgroundColor: 'var(--frs-menu-bg)',
            borderColor: 'var(--frs-border)',
          }),
        }),
        option: (provider, state) => ({
          ...provider,
          fontWeight: 400,
          ...(isNewTheme &&
            !state.isSelected && {
              color: 'var(--frs-option-text)',
              backgroundColor: state.isFocused
                ? 'var(--frs-option-hover-bg)'
                : 'transparent',
            }),
          ...(state.isSelected && {
            '> p': { color: 'white' },
          }),
        }),
        menuList: (provider) => ({
          ...provider,
          scrollbarWidth: 'auto',
          scrollbarColor: isNewTheme
            ? 'var(--frs-scrollbar)'
            : '#e3e3e3 #ffffff;',
          '&::-webkit-scrollbar': {
            width: '14px',
          },
        }),
      }}
    />
  );
};
